import React, { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useThemeToggle } from "../../context/ThemeContext";
import useMediaQuery from "../../hooks/useMediaQuery";
import { mq } from "../../styles/canonical/breakpoints";
import CardsLinhas, { dataCurta } from "./CardsLinhas";
import { formatarData } from "../../utils/calculos";
import { plural } from "../../utils/dinheiro";
import { OPERACOES_EXIBICAO, PERIODOS_HISTORICO, buildHistorico } from "./visoes";
import {
  CelulaDupla,
  DuasColunas,
  EmptyState,
  HistoricoGrid,
  LinhaClicavel,
  MultiploTitulo,
  Multiplos,
  Painel,
  PainelTitulo,
  StatusBadge,
  SummaryCard,
  SummaryGrid,
  SummaryLabel,
  SummarySub,
  SummaryValue,
  Tabela,
  TabelaWrap,
  Td,
  Th,
  TooltipBox,
  VisaoTab,
  VisaoTabs,
} from "./styles";

// Uma cor só por gráfico (small multiples: o título diz a operação), validada
// com o validador de paleta contra a superfície de cada tema. Azul = "info" na
// paleta do ERP; o --info-solid do tema escuro (#60A5FA) é claro demais para
// barra e reprovou — por isso o passo mais escuro no dark.
const COR_BARRA = { claro: "#2563EB", escuro: "#3987e5" };
const LIMITE_LISTA_DIAS = 40;
// Eixo do gráfico: prop de SVG não aceita media query, então o piso de 12px
// do celular/tablet vem pelo `compacto`; desktop segue 11px.
const TICK_FONTE = { desktop: 11, compacto: 12 };

function TooltipSemana({ active, payload, operacao }) {
  if (!active || !payload?.length) return null;
  const semana = payload[0].payload;
  const qtd = semana[operacao.chave];
  return (
    <TooltipBox>
      <strong>Semana de {formatarData(semana.inicio)}</strong>
      {plural(qtd, "ambiente", "ambientes")} com {operacao.titulo.toLowerCase()}
    </TooltipBox>
  );
}

function GraficoSemanal({ semanas, operacao, cor, compacto }) {
  const tickFonte = compacto ? TICK_FONTE.compacto : TICK_FONTE.desktop;
  const total = semanas.reduce((soma, semana) => soma + semana[operacao.chave], 0);
  return (
    <div>
      <MultiploTitulo>
        {operacao.titulo}
        <span>
          {total} no período
        </span>
      </MultiploTitulo>
      <div style={{ height: 150 }} aria-label={`${operacao.titulo} por semana`} role="img">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={semanas} margin={{ top: 8, right: 4, bottom: 0, left: 0 }} barCategoryGap="28%">
            <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: tickFonte, fill: "var(--ink-3, currentColor)" }}
              interval="preserveStartEnd"
              minTickGap={12}
            />
            <YAxis
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
              width={28}
              tick={{ fontSize: tickFonte, fill: "var(--ink-3, currentColor)" }}
            />
            <Tooltip
              cursor={{ fill: "var(--chart-grid)" }}
              content={<TooltipSemana operacao={operacao} />}
            />
            {/* Sem animação: a captura da exportação (html2canvas) pegava barras no meio do crescimento. */}
            <Bar
              dataKey={operacao.chave}
              fill={cor}
              radius={[4, 4, 0, 0]}
              maxBarSize={28}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Lotes({ eventos }) {
  if (!eventos.length) return <span style={{ opacity: 0.5 }}>—</span>;
  const lotes = [...new Set(eventos.map((ev) => ev.loteLabel.replace("Lote ", "")))];
  return (
    <span title={eventos.map((ev) => `${ev.loteLabel} · ${ev.ambiente}`).join("\n")}>
      <strong>{eventos.length}</strong>{" "}
      <span style={{ opacity: 0.7 }}>({lotes.join(", ")})</span>
    </span>
  );
}

function ProducaoPorDia({ dias }) {
  if (!dias.length) return <EmptyState>Nenhuma operação no período.</EmptyState>;
  const visiveis = dias.slice(0, LIMITE_LISTA_DIAS);
  return (
    <TabelaWrap>
      <Tabela>
        <thead>
          <tr>
            <Th style={{ width: "16%" }}>Dia</Th>
            {OPERACOES_EXIBICAO.map((op) => (
              <Th key={op.chave}>{op.titulo} (lotes)</Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visiveis.map((dia) => (
            <tr key={dia.chave}>
              <Td>{formatarData(dia.data)}</Td>
              {OPERACOES_EXIBICAO.map((op) => (
                <Td key={op.chave}>
                  <Lotes eventos={dia[op.chave]} />
                </Td>
              ))}
            </tr>
          ))}
        </tbody>
      </Tabela>
    </TabelaWrap>
  );
}

function Atrasados({ atrasados, onSelectLoteId, compacto }) {
  if (!atrasados.length) {
    return <EmptyState>Nenhum ambiente terminou as máquinas depois do limite no período.</EmptyState>;
  }
  if (compacto) {
    return (
      <CardsLinhas
        testId="historico-atrasados-cards"
        itens={atrasados.map((amb, index) => ({
          id: `${amb.loteId}-${index}`,
          titulo: amb.cliente,
          sub: amb.ambiente,
          selo: <StatusBadge $tone="danger">{amb.atrasoDias} du</StatusBadge>,
          barra: "danger",
          meta: [
            ["lote", amb.loteLabel.replace("Lote ", "")],
            ["limite", dataCurta(amb.limite)],
            ["concluído", dataCurta(amb.conclusao)],
          ],
          onAbrir: () => onSelectLoteId(amb.loteId),
        }))}
      />
    );
  }
  return (
    <TabelaWrap>
      <Tabela>
        <thead>
          <tr>
            <Th style={{ width: "14%" }}>Lote</Th>
            <Th>Cliente / Ambiente</Th>
            <Th style={{ width: "17%" }}>Limite</Th>
            <Th style={{ width: "17%" }}>Concluído</Th>
            <Th $align="right" style={{ width: "14%" }}>
              Atraso
            </Th>
          </tr>
        </thead>
        <tbody>
          {atrasados.map((amb, index) => (
            <LinhaClicavel key={`${amb.loteId}-${index}`} onClick={() => onSelectLoteId(amb.loteId)}>
              <Td>{amb.loteLabel.replace("Lote ", "")}</Td>
              <Td>
                <CelulaDupla>
                  <strong title={amb.cliente}>{amb.cliente}</strong>
                  <span title={amb.ambiente}>{amb.ambiente}</span>
                </CelulaDupla>
              </Td>
              <Td>{formatarData(amb.limite)}</Td>
              <Td>{formatarData(amb.conclusao)}</Td>
              <Td $align="right">{amb.atrasoDias} du</Td>
            </LinhaClicavel>
          ))}
        </tbody>
      </Tabela>
    </TabelaWrap>
  );
}

function TempoPorLote({ lotes, onSelectLoteId, compacto }) {
  if (!lotes.length) return <EmptyState>Nenhum lote concluiu as máquinas no período.</EmptyState>;
  if (compacto) {
    return (
      <CardsLinhas
        testId="historico-lotes-cards"
        itens={lotes.map((lote) => ({
          id: lote.loteId,
          titulo: lote.loteLabel,
          sub: lote.cliente,
          selo: null,
          meta: [
            ["amb.", lote.ambientes],
            ["mdf", dataCurta(lote.mdf)],
            ["concluído", dataCurta(lote.conclusao)],
            ["tempo", lote.tempoMaquinas != null ? `${lote.tempoMaquinas} du` : null],
            ["no prazo", lote.comLimite ? `${lote.noPrazo}/${lote.comLimite}` : null],
          ],
          onAbrir: () => onSelectLoteId(lote.loteId),
        }))}
      />
    );
  }
  return (
    <TabelaWrap>
      <Tabela>
        <thead>
          <tr>
            <Th style={{ width: "13%" }}>Lote</Th>
            <Th>Cliente</Th>
            <Th $align="center" style={{ width: "12%" }}>
              Amb.
            </Th>
            <Th style={{ width: "15%" }}>MDF chegou</Th>
            <Th style={{ width: "15%" }}>Concluído</Th>
            <Th $align="right" style={{ width: "12%" }} title="Dias úteis do MDF à última operação">
              Tempo
            </Th>
            <Th $align="right" style={{ width: "12%" }} title="Ambientes concluídos dentro do limite">
              No prazo
            </Th>
          </tr>
        </thead>
        <tbody>
          {lotes.map((lote) => (
            <LinhaClicavel key={lote.loteId} onClick={() => onSelectLoteId(lote.loteId)}>
              <Td>{lote.loteLabel.replace("Lote ", "")}</Td>
              <Td title={lote.cliente}>{lote.cliente}</Td>
              <Td $align="center">{lote.ambientes}</Td>
              <Td>{formatarData(lote.mdf)}</Td>
              <Td>{formatarData(lote.conclusao)}</Td>
              <Td $align="right">{lote.tempoMaquinas != null ? `${lote.tempoMaquinas} du` : "—"}</Td>
              <Td $align="right">{lote.comLimite ? `${lote.noPrazo}/${lote.comLimite}` : "—"}</Td>
            </LinhaClicavel>
          ))}
        </tbody>
      </Tabela>
    </TabelaWrap>
  );
}

export function SeletorPeriodo({ periodo, onChange }) {
  return (
    <VisaoTabs role="group" aria-label="Período do histórico">
      {PERIODOS_HISTORICO.map((item) => (
        <VisaoTab
          key={item.id}
          type="button"
          $active={periodo === item.id}
          aria-pressed={periodo === item.id}
          onClick={() => onChange(item.id)}
        >
          {item.label}
        </VisaoTab>
      ))}
    </VisaoTabs>
  );
}

export default function HistoricoView({ dados, filters, periodo, onSelectLoteId }) {
  const tema = useThemeToggle();
  const compacto = useMediaQuery(mq.tablet);
  const cor = tema?.isDark ? COR_BARRA.escuro : COR_BARRA.claro;
  const historico = useMemo(() => buildHistorico(dados, filters, periodo), [dados, filters, periodo]);
  const { kpis, intervalo } = historico;
  const faixa = `${formatarData(intervalo.inicio)} a ${formatarData(intervalo.fim)}`;

  return (
    <HistoricoGrid data-pdf="historico-industrializacao">
      <SummaryGrid>
        <SummaryCard>
          <SummaryLabel>Operações feitas</SummaryLabel>
          <SummaryValue>{kpis.operacoes}</SummaryValue>
          <SummarySub>cortes, bordos e furações · {faixa}</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Ambientes concluídos</SummaryLabel>
          <SummaryValue>{kpis.concluidos}</SummaryValue>
          <SummarySub>terminaram as máquinas no período</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Dentro do limite</SummaryLabel>
          <SummaryValue
            $tone={kpis.percentualNoPrazo != null && kpis.percentualNoPrazo < 80 ? "danger" : "default"}
          >
            {kpis.percentualNoPrazo != null ? `${kpis.percentualNoPrazo}%` : "—"}
          </SummaryValue>
          <SummarySub>
            {kpis.noPrazo} de {kpis.comLimite} com limite das máquinas
          </SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Tempo médio de máquinas</SummaryLabel>
          <SummaryValue>
            {kpis.tempoMedio != null ? `${String(kpis.tempoMedio).replace(".", ",")} du` : "—"}
          </SummaryValue>
          <SummarySub>dias úteis do MDF chegar à última operação</SummarySub>
        </SummaryCard>
      </SummaryGrid>

      <Painel>
        <PainelTitulo>
          Produção por semana<small>ambientes por operação, semana de segunda a domingo</small>
        </PainelTitulo>
        <Multiplos>
          {OPERACOES_EXIBICAO.map((op) => (
            <GraficoSemanal key={op.chave} semanas={historico.semanas} operacao={op} cor={cor} compacto={compacto} />
          ))}
        </Multiplos>
      </Painel>

      <DuasColunas>
        <Painel>
          <PainelTitulo>
            Terminaram fora do limite<small>{historico.atrasados.length} no período</small>
          </PainelTitulo>
          <Atrasados atrasados={historico.atrasados} onSelectLoteId={onSelectLoteId} compacto={compacto} />
        </Painel>
        <Painel>
          <PainelTitulo>
            Tempo de máquinas por lote<small>{historico.lotes.length} lotes concluídos</small>
          </PainelTitulo>
          <TempoPorLote lotes={historico.lotes} onSelectLoteId={onSelectLoteId} compacto={compacto} />
        </Painel>
      </DuasColunas>

      <Painel>
        <PainelTitulo>
          Produção por dia
          <small>
            {historico.dias.length > LIMITE_LISTA_DIAS
              ? `${LIMITE_LISTA_DIAS} dias mais recentes de ${historico.dias.length}`
              : `${historico.dias.length} dias com operação`}
          </small>
        </PainelTitulo>
        <ProducaoPorDia dias={historico.dias} />
      </Painel>
    </HistoricoGrid>
  );
}
